import facebookLogo from '@/assets/facebook_logo.png';
import pinterestLogo from '@/assets/pinterest_logo.png';
import twitterLogo from '@/assets/twitter_logo.png';
import { AccessibleDivButton, Drawer } from '@/components';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { BodySmallMidEmphasis, TitleMediumEmphasized, useSnackbar } from '@/design-system';
import { TrackingFrom, useAnalytics } from '@/hooks';
import { useIsMobile } from '@/hooks';
import { copyToClipboard, downloadFileByUrl } from '@/utils/utils';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import { IconButton, Modal, Tooltip } from '@mui/material';
import styles from './ShareFlyerDrawer.styles';

const SHARE_DIALOG_TITLE_ID = 'share-flyer-dialog-title';

interface Props {
  open: boolean;
  onClose: () => void;
  flyerTitle: string;
  thumbnail: string;
  userText: string;
  link: string;
  pdfUrl: string;
  shareUrl: string;
  trackMetrics: (action: string) => void;
  trackingFrom: TrackingFrom;
  isQuarantined?: boolean;
}

export const ShareFlyerDrawer: React.FC<Props> = ({
  open,
  onClose,
  flyerTitle,
  thumbnail,
  userText,
  link,
  pdfUrl,
  shareUrl,
  trackMetrics,
  trackingFrom,
  isQuarantined,
}) => {
  const showToast = useSnackbar();
  const showModal = !useIsMobile();
  const { trackEvent } = useAnalytics();
  const encodedShareUrl = encodeURIComponent(shareUrl);
  const encodedFlyerTitle = encodeURIComponent(flyerTitle);
  const openExternalLink = (link: string) => {
    window.open(link, '_blank');
  };

  const trackShareActions = (shareOption: string) => {
    trackEvent({
      event: TrackingEvents.shareFlyer,
      event_detail: EventDetails.shareFlyer(shareOption),
      stage: TrackingStage.consideration,
      ...trackingFrom,
    });
  };
  const socialMedias = [
    {
      name: 'Facebook',
      icon: facebookLogo,
      onClick: () => {
        const link = `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`;
        openExternalLink(link);
        trackMetrics('pa-share-facebook');
        trackShareActions('facebook');
      },
    },
    {
      name: 'X',
      icon: twitterLogo,
      onClick: () => {
        const link = `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedFlyerTitle}&hashtags=peachjar`;
        openExternalLink(link);
        trackMetrics('pa-share-x');
        trackShareActions('x');
      },
    },
    {
      name: 'Pinterest',
      icon: pinterestLogo,
      onClick: () => {
        const link = `https://pinterest.com/pin/create/button/?url=${encodedShareUrl}&media=${encodeURIComponent(
          thumbnail
        )}&description=${encodeURIComponent(encodedFlyerTitle)}`;
        openExternalLink(link);
        trackMetrics('pa-share-pinterest');
        trackShareActions('pinterest');
      },
    },
  ];

  const buttons = [
    ...(typeof navigator.share === 'function'
      ? [
          {
            name: 'Share',
            icon: <IosShareIcon />,
            onClick: () => {
              trackMetrics('pa-share-link-v2');
              trackShareActions('share_link');
              navigator.share({
                title: flyerTitle,
                text: flyerTitle,
                url: shareUrl,
              });
            },
          },
        ]
      : []),
    {
      name: 'Copy link',
      icon: <ContentCopyIcon />,
      onClick: () => {
        trackMetrics('pa-copy-link');
        trackShareActions('copy_link');
        copyToClipboard(`${link}?origin=share`);
        showToast({ message: 'Link copied to clipboard' });
      },
    },
    {
      name: 'Print',
      icon: <LocalPrintshopOutlinedIcon />,
      onClick: () => {
        trackMetrics('pa-print');
        trackEvent({
          event: TrackingEvents.export,
          event_detail: EventDetails.print,
          stage: TrackingStage.commitment,
          ...trackingFrom,
        });
        onClose();
        window.print();
      },
    },
    ...(isQuarantined === false
      ? [
          {
            name: 'Original Uploaded File',
            icon: <FileDownloadOutlinedIcon />,
            tooltipText: 'Accessibility may vary depending on the source document provided by the submitter',
            onClick: () => {
              trackMetrics('pa-download');
              trackEvent({
                event: TrackingEvents.export,
                event_detail: EventDetails.download,
                stage: TrackingStage.commitment,
                ...trackingFrom,
              });
              const fileName = 'flyer.pdf';
              downloadFileByUrl(pdfUrl, fileName);
              showToast({ message: 'Downloading...' });
            },
          },
        ]
      : []),
  ];

  const SocialMediaButtons = () => (
    <div className={styles.section}>
      {socialMedias.map(({ icon, name, onClick }) => (
        <AccessibleDivButton className={styles.buttonContainer} onClick={onClick} key={name}>
          <img src={icon} width="50px" height="50px" alt={name} />
          <BodySmallMidEmphasis>{name}</BodySmallMidEmphasis>
        </AccessibleDivButton>
      ))}
    </div>
  );

  const ActionButtons = () => (
    <div className={styles.section}>
      {buttons.map(({ icon, name, onClick, tooltipText }) => (
        <div key={name}>
          {tooltipText ? (
            <Tooltip title={tooltipText} placement="top" enterTouchDelay={0}>
              <div className={styles.buttonContainer}>
                <IconButton className={styles.button} onClick={onClick} aria-label={name}>
                  {icon}
                </IconButton>
                <BodySmallMidEmphasis>{name}</BodySmallMidEmphasis>
              </div>
            </Tooltip>
          ) : (
            <div className={styles.buttonContainer}>
              <IconButton className={styles.button} onClick={onClick} aria-label={name}>
                {icon}
              </IconButton>
              <BodySmallMidEmphasis>{name}</BodySmallMidEmphasis>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const Content = () => (
    <div className={styles.content}>
      <div className={styles.flyer}>
        <img
          src={thumbnail}
          alt={
            userText
              ? `Flyer preview titled ${flyerTitle}. Text on flyer: ${userText}`
              : `Flyer preview titled ${flyerTitle}`
          }
          height="233px"
          width="100%"
        />
      </div>
      <SocialMediaButtons />
      <ActionButtons />
    </div>
  );

  const ShareDrawer = () => (
    <Drawer title="Share" isOpen={open} onToggle={onClose}>
      <Content />
    </Drawer>
  );

  const ShareModal = () => (
    <Modal open={open} onClose={onClose}>
      <div
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby={SHARE_DIALOG_TITLE_ID}
      >
        <div className={styles.modalTitleContainer}>
          <AccessibleDivButton className={styles.closeButton} onClick={onClose} aria-label="Close">
            <CloseOutlinedIcon />
          </AccessibleDivButton>
          <TitleMediumEmphasized as="h1" id={SHARE_DIALOG_TITLE_ID}>
            Share
          </TitleMediumEmphasized>
        </div>
        <Content />
      </div>
    </Modal>
  );

  return showModal ? <ShareModal /> : <ShareDrawer />;
};
